import { DialogRef } from '@angular/cdk/dialog';
import { Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subscription, fromEvent, interval } from 'rxjs';
import { NoconnectComponent } from '../../shared/components/noconnect/noconnect.component';
import { SessionProvider } from './session.provider';

@Injectable({
  providedIn: 'root'
})
export class CheckOnlineService {
  connectivityState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  backendState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  offlineEvent: Observable<Event>;
  onlineEvent: Observable<Event>;
  subscriptions: Subscription[] = [];

  isNewVersionAvailable: boolean = false;
  backendIntervalSource = interval(30 * 1000);
  intervalSubscription: Subscription;

  failModal: MatDialogRef<NoconnectComponent> | null = null;

  constructor(
    private zone: NgZone,
    private modalService: MatDialog,
    private session: SessionProvider)
  {
    this.subscribeToOnlineStateChanges();
    this.startBackendCheck();
  }

  subscribeToOnlineStateChanges() {
    this.onlineEvent = fromEvent(window, 'online');
    this.offlineEvent = fromEvent(window, 'offline');

    this.subscriptions.push(this.onlineEvent.subscribe(e => {
      this.backendCheck().then(_ => {
        this.connectivityState$.next(true);
      });
    }));

    this.subscriptions.push(this.offlineEvent.subscribe(e => {
      this.connectivityState$.next(false);
      this.showModalMessageOnConnectionFail();
    }));
  }

  startBackendCheck(): void {
    this.intervalSubscription?.unsubscribe();

    this.zone.runOutsideAngular(() => {
      this.intervalSubscription = this.backendIntervalSource.subscribe(async () => {
        try {
          this.backendCheck();
          this.checkForUpdate();
        } catch (error) {
          console.error('Failed to check backend state:', error);
        }
      });
    })
  }

  async backendCheck():Promise<void> {
    const backendState = await this.baseAddressIsReachable();
    if (backendState !== this.backendState$.value) {
      this.backendState$.next(backendState);
      if (backendState === false) {
        this.showModalMessageOnConnectionFail();
      }
    }
  }
  async baseAddressIsReachable(): Promise<boolean> {
    return fetch("", { method: 'HEAD', mode: 'no-cors' })
      .then(function (resp) {
        return resp && (resp.ok || resp.type === 'opaque');
      })
      .catch(function (err) {
        console.warn('[conn test failure]:', err);
        return false;
      });
  }
  async checkForUpdate(autoUpdate: boolean = false): Promise<void> {
    try {          
          const resp = await fetch("", { method: 'HEAD', mode: 'no-cors' });
          const serverVersion = resp.headers.get('x-appversion');
          const clientVersion = this.session.getAppVersion();

          if (serverVersion && serverVersion !== clientVersion) {
              console.log('A new version is available.');
              setTimeout(() => {
                if (autoUpdate) {
                  setTimeout(() => {
                    document.location.reload();
                  }, 2000);
                } else {
                  this.session.versionMismatch(serverVersion);
                }
              }, 1000);
          } else {
              console.log('Already on the latest version.');
          }
      } catch (error) {
          console.error('Failed to check for updates:', error);
      }
  }

  showModalMessageOnConnectionFail() {
    if (this.failModal === null || this.failModal.getState() === MatDialogState.CLOSED) {
      this.failModal = this.modalService.open(NoconnectComponent, {
        disableClose: true,
        panelClass: 'noconnect-modal',
        backdropClass: 'noconnect-backdrop'
      });
    }
  }
}
